import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#mediaObserver.observe(this, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      childList: true,
      subtree: true,
    });
    this.#hydrateActiveSlides();
    this.#setupDeferredGrid();
  }

  #controller = new AbortController();
  #mediaObserver = new MutationObserver(() => this.#hydrateActiveSlides());
  #gridObserver;
  #zoomDialogPromise;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#mediaObserver.disconnect();
    this.#gridObserver?.disconnect();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async zoom(index, event) {
    const zoomDialog = await this.#ensureZoomDialog();
    const zoomMedia = zoomDialog?.refs?.media || [];

    for (const mediaIndex of [index - 1, index, index + 1]) {
      hydrateDeferredImages(zoomMedia[mediaIndex]);
    }

    zoomDialog?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialog = this.querySelector(':scope > zoom-dialog');
    const zoomDialogMedia = zoomDialog?.refs.media[index];
    if (!zoomDialogMedia) return;

    zoomDialog?.loadHighResolutionImage(zoomDialogMedia);
  }

  /**
   * Hydrates the selected mobile slide and its immediate neighbours.
   * @param {Element | undefined} target
   */
  hydrateMediaAround(target) {
    const slides = this.#slideshowSlides;
    const targetIndex = target ? slides.indexOf(target) : slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false');
    const activeIndex = targetIndex >= 0 ? targetIndex : 0;

    for (let index = Math.max(0, activeIndex - 1); index <= Math.min(slides.length - 1, activeIndex + 1); index += 1) {
      hydrateDeferredImages(slides[index]);
    }
  }

  /**
   * Appends deferred slideshow markup until a requested slide is available.
   * Images in appended slides keep inert data-src attributes until selected.
   * @param {number|string|{id: string}} input
   */
  async ensureMediaLoaded(input) {
    const requestedIndex =
      typeof input === 'number' ? input : typeof input === 'string' ? Number.parseInt(input, 10) : undefined;
    const requestedId = typeof input === 'object' && input && 'id' in input ? `${input.id}` : undefined;
    const loadAll = requestedIndex === Number.POSITIVE_INFINITY;

    if (requestedId && this.querySelector(`slideshow-slide[slide-id="${CSS.escape(requestedId)}"]`)) return;
    if (Number.isFinite(requestedIndex) && requestedIndex < this.#slideshowSlides.length) return;

    const template = this.querySelector(':scope > template[data-deferred-gallery-slides]');
    if (!(template instanceof HTMLTemplateElement)) return;

    while (template.content.firstElementChild) {
      this.#appendDeferredSlides(10);

      if (requestedId && this.querySelector(`slideshow-slide[slide-id="${CSS.escape(requestedId)}"]`)) break;
      if (!loadAll && Number.isFinite(requestedIndex) && requestedIndex < this.#slideshowSlides.length) break;
      if (!loadAll && !requestedId && !Number.isFinite(requestedIndex)) break;
    }

    await Promise.resolve();
  }

  get #slideshowSlides() {
    return Array.from(this.querySelectorAll(':scope > slideshow-component slideshow-slide[data-media-id]'));
  }

  #hydrateActiveSlides() {
    this.hydrateMediaAround();

    const slides = this.#slideshowSlides;
    const activeIndex = slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false');
    if (activeIndex >= slides.length - 3) this.#appendDeferredSlides(10);
  }

  /** @param {number} count */
  #appendDeferredSlides(count) {
    const template = this.querySelector(':scope > template[data-deferred-gallery-slides]');
    const scroller = this.querySelector(':scope > slideshow-component slideshow-slides');
    if (!(template instanceof HTMLTemplateElement) || !scroller) return 0;

    const fragment = document.createDocumentFragment();
    let appended = 0;
    while (template.content.firstElementChild && appended < count) {
      fragment.append(template.content.firstElementChild);
      appended += 1;
    }
    scroller.append(fragment);

    if (!template.content.firstElementChild) template.remove();
    return appended;
  }

  #setupDeferredGrid() {
    const sentinel = this.querySelector('[data-deferred-gallery-grid-sentinel]');
    if (!sentinel) return;

    this.#gridObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#appendDeferredGrid(10);
      },
      { rootMargin: '800px 0px' }
    );
    this.#gridObserver.observe(sentinel);
  }

  /** @param {number} count */
  #appendDeferredGrid(count) {
    const template = this.querySelector(':scope > template[data-deferred-gallery-grid]');
    const sentinel = this.querySelector('[data-deferred-gallery-grid-sentinel]');
    if (!(template instanceof HTMLTemplateElement) || !sentinel?.parentElement) return;

    const fragment = document.createDocumentFragment();
    let appended = 0;
    while (template.content.firstElementChild && appended < count) {
      fragment.append(template.content.firstElementChild);
      appended += 1;
    }
    sentinel.before(fragment);

    if (!template.content.firstElementChild) {
      this.#gridObserver?.disconnect();
      sentinel.remove();
      template.remove();
    }
  }

  async #ensureZoomDialog() {
    const existingDialog = this.querySelector(':scope > zoom-dialog');
    if (existingDialog) return existingDialog;
    if (this.#zoomDialogPromise) return this.#zoomDialogPromise;

    await this.ensureMediaLoaded(Number.POSITIVE_INFINITY);

    this.#zoomDialogPromise = this.#buildZoomDialog();
    return this.#zoomDialogPromise;
  }

  async #buildZoomDialog() {
    const template = this.querySelector(':scope > template[data-zoom-dialog-template]');
    if (!(template instanceof HTMLTemplateElement)) return undefined;

    const moduleSources = [this.dataset.zoomDialogSrc, this.dataset.dragZoomSrc].filter(Boolean);
    await Promise.all(moduleSources.map((source) => import(new URL(source, document.baseURI).href)));

    const fragment = template.content.cloneNode(true);
    const zoomDialog = fragment.querySelector('zoom-dialog');
    const thumbnails = fragment.querySelector('[ref="thumbnails"]');
    const mediaList = fragment.querySelector('[data-zoom-media-list]');
    if (!zoomDialog || !thumbnails || !mediaList) return undefined;

    const slides = this.#slideshowSlides.sort(
      (first, second) => Number(first.dataset.originalIndex) - Number(second.dataset.originalIndex)
    );

    slides.forEach((slide, index) => {
      const sourceMedia = slide.querySelector(':scope > .product-media, :scope > .product-media-constraint-wrapper > .product-media');
      const previewImage = sourceMedia?.querySelector('img.product-media__image');
      if (!sourceMedia || !previewImage) return;

      const aspectRatio = sourceMedia.style.getPropertyValue('--ratio') || '1';
      const thumbnailButton = document.createElement('button');
      thumbnailButton.type = 'button';
      thumbnailButton.setAttribute('role', 'tab');
      thumbnailButton.className = 'button button-unstyled dialog-thumbnails-list__thumbnail';
      thumbnailButton.setAttribute('aria-label', previewImage.alt || `Image ${index + 1}`);
      thumbnailButton.setAttribute('aria-selected', `${index === 0}`);
      thumbnailButton.setAttribute('on:click', `/handleThumbnailClick/${index}`);
      thumbnailButton.setAttribute('on:pointerenter', `/handleThumbnailPointerEnter/${index}`);
      thumbnailButton.style.cssText = `--aspect-ratio: ${aspectRatio}; --gallery-aspect-ratio: ${aspectRatio};`;

      const thumbnailImage = document.createElement('img');
      thumbnailImage.src = getResizedImageUrl(previewImage, 128);
      thumbnailImage.alt = previewImage.alt;
      thumbnailImage.loading = 'lazy';
      thumbnailImage.decoding = 'async';
      thumbnailImage.fetchPriority = 'low';
      thumbnailImage.style.cssText = previewImage.style.cssText;
      thumbnailButton.append(thumbnailImage);
      thumbnails.append(thumbnailButton);

      const mediaItem = document.createElement('li');
      const mediaId = slide.dataset.mediaId || sourceMedia.dataset.mediaId || index;
      mediaItem.id = `product-${mediaId}-${index + 1}`;
      mediaItem.className = slide.className.replace('media-fit-cover', '');
      mediaItem.style.setProperty('--media-preview-ratio', aspectRatio);
      mediaItem.setAttribute('ref', 'media[]');

      const mediaClone = sourceMedia.cloneNode(true);
      const clonedImage = mediaClone.querySelector('img.product-media__image');
      clonedImage?.setAttribute('ref', 'image');

      if (clonedImage) {
        mediaItem.setAttribute('on:click', '/close');
        const dragWrapper = document.createElement('drag-zoom-wrapper');
        dragWrapper.className = 'product-media__drag-zoom-wrapper';
        dragWrapper.append(mediaClone);
        mediaItem.append(dragWrapper);
      } else {
        mediaItem.append(mediaClone);
      }

      mediaList.append(mediaItem);
    });

    template.before(fragment);
    template.remove();
    await customElements.whenDefined('zoom-dialog');
    await Promise.resolve();

    const connectedDialog = this.querySelector(':scope > zoom-dialog');
    connectedDialog?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal: this.#controller.signal,
    });
    return connectedDialog;
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

/** @param {Element | undefined} root */
function hydrateDeferredImages(root) {
  if (!root) return;

  root.querySelectorAll('img[data-src]').forEach((image) => {
    if (!(image instanceof HTMLImageElement)) return;

    const source = image.dataset.src;
    const sourceSet = image.dataset.srcset;
    if (sourceSet) image.srcset = sourceSet;
    if (source) image.src = source;
    image.removeAttribute('data-src');
    image.removeAttribute('data-srcset');
  });
}

/**
 * @param {HTMLImageElement} image
 * @param {number} width
 */
function getResizedImageUrl(image, width) {
  const source = image.getAttribute('data_max_resolution') || image.dataset.src || image.currentSrc || image.src;
  const url = new URL(source, document.baseURI);
  url.searchParams.set('width', `${width}`);
  return url.href;
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
